import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import {
  deleteOrder,
  fetchOrderDetailsByOrderId,
  fetchOrderDetailsList,
  fetchOrdersList,
  normalizeOrderDetail,
  saveOrder,
  saveOrderDetail
} from "../../shared/commerceMastersApi";
import { fetchCustomerAddressesList, fetchCustomersForLookup } from "../../shared/customerMastersApi";
import { IconEye } from "../../shared/toolbarIcons";
import { OrderDetailModal } from "../../shared/OrderDetailModal";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";
import { normalizeOrderItemStatus, ORDER_ITEM_STATUSES } from "../../shared/orderItemStatuses";
import { normalizePaymentStatus, PAYMENT_STATUSES } from "../../shared/paymentStatuses";
import { formatLocation } from "../../shared/formatLocation";

function formatDateTime(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function truncate(s, max) {
  if (s == null || s === "") return "—";
  const t = String(s);
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

function OrderProductsCell({ lines }) {
  if (!lines?.length) return <span>—</span>;
  const names = [...new Set(lines.map((line) => line.productName).filter(Boolean))];
  const visible = names.slice(0, 3);
  const extra = names.length - visible.length;
  return (
    <div className="order-products-cell order-products-cell--names-only">
      {visible.map((name) => (
        <span key={name} className="order-products-cell__name" title={name}>
          {name}
        </span>
      ))}
      {extra > 0 && <span className="order-products-cell__more">+{extra} more</span>}
    </div>
  );
}

function OrderItemStatusCell({ lines, onStatusChange, savingLineId }) {
  if (!lines?.length) return <span>—</span>;
  return (
    <div className="order-item-status-cell" onClick={(e) => e.stopPropagation()}>
      {lines.map((line) => (
        <div key={line.id} className="order-item-status-cell__row">
          {lines.length > 1 ? (
            <span className="order-item-status-cell__label" title={line.productName}>
              {truncate(line.productName, 18)}
            </span>
          ) : null}
          <div className="order-item-status-cell__select-wrap">
            <select
              className="order-item-status-cell__select"
              value={line.itemStatus}
              disabled={savingLineId === line.id}
              aria-label={`Item status for ${line.productName}`}
              onChange={(e) => onStatusChange(line, e.target.value)}
            >
              {ORDER_ITEM_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      ))}
    </div>
  );
}

function OrderPaymentStatusCell({ order, onStatusChange, savingOrderId }) {
  const paymentStatus = normalizePaymentStatus(order.paymentStatus);
  return (
    <div className="order-item-status-cell" onClick={(e) => e.stopPropagation()}>
      <div className="order-item-status-cell__select-wrap">
        <select
          className="order-item-status-cell__select"
          value={paymentStatus}
          disabled={savingOrderId === order.id}
          aria-label={`Payment status for order ${order.orderNo || order.id}`}
          onChange={(e) => onStatusChange(order, e.target.value)}
        >
          {PAYMENT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export function OrdersList() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [paymentSavingId, setPaymentSavingId] = useState(null);

  const customerById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(Number(c.id), c);
    return m;
  }, [customers]);

  const addressById = useMemo(() => {
    const m = new Map();
    for (const a of customerAddresses) m.set(Number(a.id), a);
    return m;
  }, [customerAddresses]);

  const productById = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(Number(p.id ?? p.Id), p);
    return m;
  }, [products]);

  const orderDetailsByOrderId = useMemo(() => {
    const m = new Map();
    for (const d of orderDetails) {
      const orderId = Number(d.orderId ?? d.OrderId);
      if (!Number.isFinite(orderId)) continue;
      const list = m.get(orderId) ?? [];
      list.push(d);
      m.set(orderId, list);
    }
    return m;
  }, [orderDetails]);

  const rows = useMemo(() => {
    return items.map((x) => {
      const customer = customerById.get(Number(x.customerId));
      const addr = addressById.get(Number(x.customerAddressId));
      const shipToFull = addr?.address ?? "";
      const shipTo =
        shipToFull !== ""
          ? truncate(shipToFull, 24)
          : x.customerAddressId != null
            ? `#${x.customerAddressId}`
            : "—";
      const lines = (orderDetailsByOrderId.get(Number(x.id)) ?? [])
        .map((line) => normalizeOrderDetail(line))
        .filter(Boolean)
        .map((line) => {
          const product = productById.get(Number(line.productId));
          return {
            ...line,
            productName: product?.name ?? (line.productId != null ? `Product #${line.productId}` : "—"),
            productImage: product?.image ?? ""
          };
        });
      const quantity = lines.length;
      return {
        id: x.id,
        order: x,
        customer,
        customerName: customer?.name ?? (x.customerId != null ? `#${x.customerId}` : "—"),
        customerEmail: customer?.email ?? "—",
        customerPhone: customer?.phoneNo ?? "—",
        shipTo,
        shipToFull,
        address: addr ?? null,
        productLines: lines,
        quantity
      };
    });
  }, [items, customerById, addressById, orderDetailsByOrderId, productById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [iRes, aRes, countryRes, stateRes, cityRes, pRes, dRes] = await Promise.all([
      fetchOrdersList(),
      fetchCustomerAddressesList(),
      apiGet("/api/Countries"),
      apiGet("/api/States"),
      apiGet("/api/Cities"),
      apiGet("/api/Products"),
      fetchOrderDetailsList()
    ]);
    if (!iRes.ok) {
      setError(iRes.error ?? "Failed to load orders");
      setItems([]);
    } else {
      setItems(iRes.items);
    }
    const orderCustomerIds = iRes.ok ? iRes.items.map((x) => x.customerId).filter((id) => id != null) : [];
    const cRes = await fetchCustomersForLookup(orderCustomerIds);
    if (!cRes.ok) {
      setError((prev) => prev ?? cRes.error ?? "Failed to load customers");
      setCustomers([]);
    } else {
      setCustomers(cRes.items);
    }
    if (!aRes.ok) {
      setError((prev) => prev ?? aRes.error ?? "Failed to load customer addresses");
      setCustomerAddresses([]);
    } else {
      setCustomerAddresses(aRes.items);
    }
    if (countryRes.ok) setCountries(countryRes.data ?? []);
    if (stateRes.ok) setStates(stateRes.data ?? []);
    if (cityRes.ok) setCities(cityRes.data ?? []);
    if (pRes.ok) setProducts(pRes.data ?? []);
    if (dRes.ok) setOrderDetails(dRes.items);
    setLoading(false);
  }, []);

  const handleItemStatusChange = useCallback(async (line, newStatus) => {
    const itemStatus = normalizeOrderItemStatus(newStatus);
    if (itemStatus === line.itemStatus) return;

    setStatusSavingId(line.id);
    setError(null);
    const res = await saveOrderDetail({ ...line, itemStatus });
    setStatusSavingId(null);

    if (!res.ok) {
      setError(res.error ?? "Failed to update item status");
      return;
    }

    setOrderDetails((prev) =>
      prev.map((detail) => {
        const normalized = normalizeOrderDetail(detail);
        if (!normalized || normalized.id !== line.id) return detail;
        return { ...detail, itemStatus, ItemStatus: itemStatus };
      })
    );
  }, []);

  const handlePaymentStatusChange = useCallback(async (order, newStatus) => {
    const paymentStatus = normalizePaymentStatus(newStatus);
    if (paymentStatus === normalizePaymentStatus(order.paymentStatus)) return;

    setPaymentSavingId(order.id);
    setError(null);
    const res = await saveOrder({ ...order, paymentStatus });
    setPaymentSavingId(null);

    if (!res.ok) {
      setError(res.error ?? "Failed to update payment status");
      return;
    }

    setItems((prev) =>
      prev.map((item) => {
        if (Number(item.id ?? item.Id) !== Number(order.id)) return item;
        return { ...item, paymentStatus, PaymentStatus: paymentStatus };
      })
    );
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this order?"))) return;
      const res = await deleteOrder(id);
      if (!res.ok) setError(res.error ?? "Failed to delete order");
      else await load();
    },
    [load]
  );

  const columns = useMemo(
    () => [
      {
        field: "orderId",
        headerName: "Id",
        width: 72,
        valueGetter: (_v, row) => row.order.id
      },
      {
        field: "date",
        headerName: "Date",
        minWidth: 130,
        valueGetter: (_v, row) => formatDateTime(row.order.date)
      },
      {
        field: "orderNo",
        headerName: "Order no",
        minWidth: 100,
        valueGetter: (_v, row) => row.order.orderNo || `#${row.order.id}`
      },
      {
        field: "customerName",
        headerName: "Customer",
        flex: 0.85,
        minWidth: 110,
        valueGetter: (_v, row) => row.customerName
      },
      {
        field: "products",
        headerName: "Products",
        flex: 1.1,
        minWidth: 180,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => <OrderProductsCell lines={params.row.productLines} />
      },
      {
        field: "quantity",
        headerName: "Qty",
        width: 72,
        align: "center",
        headerAlign: "center",
        valueGetter: (_v, row) => row.quantity ?? 0
      },
      {
        field: "itemStatus",
        headerName: "Item status",
        minWidth: 190,
        flex: 0.9,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <OrderItemStatusCell
            lines={params.row.productLines}
            savingLineId={statusSavingId}
            onStatusChange={handleItemStatusChange}
          />
        )
      },
      {
        field: "shipTo",
        headerName: "Ship-to",
        flex: 0.9,
        minWidth: 110,
        renderCell: (params) => (
          <span title={params.row.shipToFull || undefined} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {params.row.shipTo}
          </span>
        )
      },
      {
        field: "paymentStatus",
        headerName: "Pay status",
        minWidth: 130,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <OrderPaymentStatusCell
            order={params.row.order}
            savingOrderId={paymentSavingId}
            onStatusChange={handlePaymentStatusChange}
          />
        )
      },
      {
        field: "paymentMethod",
        headerName: "Pay method",
        minWidth: 100,
        valueGetter: (_v, row) => row.order.paymentMethod ?? "—"
      },
      {
        field: "subTotal",
        headerName: "Subtotal",
        width: 88,
        valueGetter: (_v, row) => row.order.subTotal ?? 0
      },
      {
        field: "grandTotal",
        headerName: "Grand",
        width: 80,
        valueGetter: (_v, row) => row.order.grandTotal ?? 0
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 110,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const x = params.row.order;
          return (
            <div className="table-actions">
              <button
                className="icon-button"
                type="button"
                title="View"
                onClick={async () => {
                  const [orderRes, detailsRes] = await Promise.all([
                    apiGet(`/api/Orders/${x.id}`),
                    fetchOrderDetailsByOrderId(x.id)
                  ]);
                  if (!orderRes.ok) {
                    setError(orderRes.error ?? "Failed to load order");
                    return;
                  }
                  if (!detailsRes.ok) {
                    setError(detailsRes.error ?? "Failed to load order lines");
                    return;
                  }
                  const address =
                    params.row.address ?? addressById.get(orderRes.data.customerAddressId) ?? null;
                  setViewData({
                    order: orderRes.data,
                    details: detailsRes.items,
                    customer: params.row.customer ?? customerById.get(orderRes.data.customerId) ?? null,
                    address,
                    location: formatLocation(address, countries, states, cities)
                  });
                }}
              >
                <IconEye />
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(x.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [handleDeleteClick, customerById, addressById, countries, states, cities, handleItemStatusChange, handlePaymentStatusChange, statusSavingId, paymentSavingId]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Orders</h2>
          <p>{loading ? "Loading..." : `Total: ${total}`}</p>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
      <OrderDetailModal
        order={viewData?.order ?? null}
        details={viewData?.details ?? []}
        customer={viewData?.customer ?? null}
        address={viewData?.address ?? null}
        location={viewData?.location ?? "—"}
        productsById={productById}
        onClose={() => setViewData(null)}
      />
    </div>
  );
}
